/**
 * Monotonic sequence for tenant/app switch requests.
 *
 * Switch requests are not serialised -- NetworkManger deliberately exempts the switch
 * URL from its cancel-all logic -- so several can be in flight at once and only the
 * latest may commit its tenant/JWT or lower the isUpdating guard.
 *
 * This counter deliberately lives outside redux. FETCH_DATA_FAILURE, LOGIN_FAILURE and
 * LOGOUT all return initialState, so a counter held in user state would reset to 0 and
 * a later switch could reuse an id an older in-flight switch still holds -- letting the
 * stale one pass the supersession check and commit over the live one.
 */
let sequence = 0;

/** Claims the next id. Call immediately before dispatching TENANT_SWITCH_START. */
export const beginSwitch = () => {
  sequence += 1;
  return sequence;
};

/** True only for the most recently claimed switch. */
export const isLatestSwitch = (switchId) => sequence === switchId;

/**
 * Desired (tenant, app) pair -- the intent behind whatever switch is in flight.
 *
 * Every switch request carries BOTH a tenant and an app, and callers fill in the one they
 * are not changing from *committed* state: the header sends (newTenant, committedApp),
 * route initialisers send (committedTenant, newApp). Committed state is exactly what is
 * stale while a switch is pending, so two overlapping callers each ask for a pair that
 * discards the other's change, and whichever claims "latest" silently wins.
 *
 * Coalescing fixes this: a requested value that differs from committed is an explicit
 * intent; one that merely echoes committed defers to any pending intent for that
 * dimension. Every request is therefore sent for the full desired pair, and the latest
 * one to land commits exactly what the user asked for across both dimensions.
 */
let intent = { tenantCd: null, appCode: null };

/** Records an explicit intent for one or both dimensions (e.g. at header selection time). */
export const registerIntent = ({ tenantCd, appCode } = {}) => {
  if (tenantCd) intent.tenantCd = tenantCd;
  if (appCode) intent.appCode = appCode;
};

/**
 * Resolves the pair a switch request should actually ask for, and makes that pair the
 * live intent so later callers coalesce onto it.
 */
export const resolveSwitchTarget = (requested = {}, committed = {}, explicit = {}) => {
  // Callers say which dimensions they actually chose; nothing is inferred by comparing the
  // request with committed state. `requested === committed` cannot tell an intentional
  // re-selection of the committed value (A/FO -> A/MASTER_DATA pending -> back to A/FO)
  // from a caller merely echoing it -- and the re-selection is exactly what must win.
  // Omitted flags default to explicit, so an un-annotated caller sends what it passed.
  const explicitTenant = explicit.tenant !== false;
  const explicitApp = explicit.app !== false;
  const tenantCd = explicitTenant
    ? (requested.tenantCd || committed.tenantCd)
    : (intent.tenantCd || requested.tenantCd || committed.tenantCd);
  const appCode = explicitApp
    ? (requested.appCode || committed.appCode)
    : (intent.appCode || requested.appCode || committed.appCode);
  intent = { tenantCd, appCode };
  return { tenantCd, appCode };
};

/**
 * The in-flight target, for guards that would otherwise compare against committed state
 * (e.g. "only request FO if we are not already on FO"). While a switch is pending the
 * committed app is stale, so such a guard must look at where we are heading instead.
 */
export const getPendingIntent = () => ({ ...intent });

/** Call when the LATEST switch settles (commit or failure) so intent re-derives from committed state. */
export const clearIntent = () => {
  intent = { tenantCd: null, appCode: null };
};

/** Test-only: restores the sequence and intent so ids are predictable per test. */
export const resetSwitchSequence = () => {
  sequence = 0;
  clearIntent();
};
